import type { ReactNode } from "react";

const CONTACT_EMAIL = "hello@phrasio.example";

function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="shell" style={{ padding: "4rem 0 6rem", maxWidth: "44rem" }}>
      <p><a href="#top">← Back to Phrasio</a></p>
      <h1>{title}</h1>
      {children}
      <p>Questions: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
    </main>
  );
}

export function LegalPage({ page }: { page: "terms" | "privacy" | "refunds" | "success" | "cancel" }) {
  if (page === "success") {
    return (
      <LegalLayout title="Payment received">
        <p>If checkout completed, your uses appear after Paddle confirms the payment. This page is not proof of payment.</p>
        <p><a href="#workspace">Return to the workspace</a></p>
      </LegalLayout>
    );
  }
  if (page === "cancel") {
    return (
      <LegalLayout title="Checkout cancelled">
        <p>No charge was completed. You can start checkout again from pricing when you are ready.</p>
        <p><a href="#pricing">Back to pricing</a></p>
      </LegalLayout>
    );
  }
  if (page === "privacy") {
    return (
      <LegalLayout title="Privacy Policy">
        <p>Phrasio is a writing editor. We collect account email, authentication data, usage counts, and the drafts you submit for rewrite so we can run the product.</p>
        <p>Paid checkout is processed by Paddle, who acts as Merchant of Record. Paddle receives the payment details needed to complete that purchase. We do not store card numbers.</p>
        <p>We do not sell your drafts or use them to train public models. We keep them only as needed to provide history, support, and security, then delete or anonymize them when they are no longer required.</p>
        <p>Contact us at {CONTACT_EMAIL} to request access or deletion of your account data.</p>
      </LegalLayout>
    );
  }
  if (page === "refunds") {
    return (
      <LegalLayout title="Refund and Cancellation Policy">
        <p>The Student pack is a one-time purchase of three successful rewrite uses for RM12, plus any tax Paddle shows at checkout.</p>
        <p>If a paid rewrite fails, that use is not consumed. If you were charged in error or cannot use the pack because of a platform fault, email {CONTACT_EMAIL} within 14 days of purchase. Approved refunds are processed by Paddle.</p>
        <p>Unused uses do not expire during the current MVP. There is no subscription to cancel.</p>
      </LegalLayout>
    );
  }
  return (
    <LegalLayout title="Terms and Conditions">
      <p>Phrasio helps you rewrite text so it reads more naturally while aiming to keep meaning, citations, and key claims intact. It does not guarantee any AI-detector score.</p>
      <p>You must have the right to submit the text you paste. You remain responsible for the final work you submit to a school or publisher, including academic-integrity rules.</p>
      <p>Paid packs are sold through Paddle as Merchant of Record. Site prices are listed in Malaysian Ringgit before tax. Checkout shows the amount you pay, including tax where Paddle applies it.</p>
      <p>We may suspend accounts that abuse the service, reverse-engineer the provider, or attempt to steal usage.</p>
    </LegalLayout>
  );
}

export function legalHashPage(hash: string): "terms" | "privacy" | "refunds" | "success" | "cancel" | null {
  const value = hash.replace(/^#/, "").split("?")[0];
  if (value === "terms") return "terms";
  if (value === "privacy") return "privacy";
  if (value === "refunds") return "refunds";
  if (value === "checkout/success") return "success";
  if (value === "checkout/cancel") return "cancel";
  return null;
}

export { CONTACT_EMAIL };
